import { z } from 'zod';

// ─────────── Fase 6 · Mídia com IA (docs/09) ───────────

export const TIPOS_MIDIA_IA = ['foto', 'anuncio', 'descricao_site', 'post'] as const;
export type TipoMidiaIa = (typeof TIPOS_MIDIA_IA)[number];
export const NOME_TIPO_MIDIA: Record<TipoMidiaIa, string> = {
  foto: 'Foto tratada',
  anuncio: 'Texto do anúncio (portais)',
  descricao_site: 'Descrição do site',
  post: 'Post para redes sociais',
};

export const STATUS_MIDIA_IA = [
  'pendente',
  'processando',
  'concluido',
  'falhou',
  'aprovado',
  'descartado',
] as const;
export type StatusMidiaIa = (typeof STATUS_MIDIA_IA)[number];
export const NOME_STATUS_MIDIA: Record<StatusMidiaIa, string> = {
  pendente: 'Na fila',
  processando: 'Gerando',
  concluido: 'Pronto para revisar',
  falhou: 'Não deu certo',
  aprovado: 'Em uso',
  descartado: 'Descartado',
};
/** Itens que não contam no limite do mês (falha da IA nunca consome o plano). */
export const STATUS_FORA_DO_LIMITE: readonly StatusMidiaIa[] = ['falhou'];

export const FUNDOS_FOTO = ['manter', 'branco', 'showroom', 'remover'] as const;
export type FundoFoto = (typeof FUNDOS_FOTO)[number];
export const NOME_FUNDO: Record<FundoFoto, string> = {
  manter: 'Manter o fundo',
  branco: 'Fundo branco',
  showroom: 'Fundo de showroom',
  remover: 'Sem fundo (transparente)',
};

export const TONS_TEXTO = ['direto', 'entusiasmado', 'sofisticado'] as const;
export type TomTexto = (typeof TONS_TEXTO)[number];
export const NOME_TOM: Record<TomTexto, string> = {
  direto: 'Direto ao ponto',
  entusiasmado: 'Entusiasmado',
  sofisticado: 'Sofisticado',
};

export const tratarFotosSchema = z.object({
  fotoIds: z.array(z.string().uuid()).min(1, 'Escolha ao menos uma foto').max(20),
  fundo: z.enum(FUNDOS_FOTO).default('manter'),
  enquadrar: z.boolean().default(true),
  luz: z.boolean().default(true),
});
export type TratarFotos = z.infer<typeof tratarFotosSchema>;

export const gerarTextoSchema = z.object({
  tipo: z.enum(['anuncio', 'descricao_site', 'post']),
  tom: z.enum(TONS_TEXTO).default('direto'),
});
export type GerarTexto = z.infer<typeof gerarTextoSchema>;

/** Limites de tamanho dos textos (portais costumam aceitar até ~3.000 caracteres). */
export const MAX_TEXTO_MIDIA = 3_000;
export const aprovarTextoSchema = z.object({
  texto: z.string().trim().min(20, 'Texto muito curto').max(MAX_TEXTO_MIDIA),
});

/** Custo em micro-reais (1 real = 1.000.000) → texto em reais com centavos. */
export const reaisDeMicros = (micros: number) =>
  (micros / 1_000_000).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });

/** Dados públicos do carro usados nos textos (nunca custo, placa, chassi ou dados de clientes). */
export interface CarroParaTexto {
  marca: string;
  modelo: string;
  versao: string | null;
  anoFabricacao: number;
  anoModelo: number;
  km: number;
  cor: string | null;
  cambio: string | null;
  combustivel: string | null;
  opcionais: string[];
  precoCentavos: number | null;
}

/**
 * Textos de modelo (sem IA externa): usados no modo de demonstração e como reserva quando o
 * provedor de IA falha. Determinísticos para o mesmo carro, tom e tipo.
 */
export function textoModelo(
  tipo: Exclude<TipoMidiaIa, 'foto'>,
  tom: TomTexto,
  c: CarroParaTexto,
  loja: { nome: string; cidade: string | null },
): string {
  const nome = `${c.marca} ${c.modelo}${c.versao ? ` ${c.versao}` : ''}`;
  const ano = `${c.anoFabricacao}/${c.anoModelo}`;
  const km = `${new Intl.NumberFormat('pt-BR').format(c.km)} km`;
  const preco = c.precoCentavos
    ? (c.precoCentavos / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        maximumFractionDigits: 0,
      })
    : null;
  const detalhes = [
    c.cor && `cor ${c.cor.toLowerCase()}`,
    c.cambio && `câmbio ${c.cambio.toLowerCase()}`,
    c.combustivel && `motor ${c.combustivel.toLowerCase()}`,
  ].filter(Boolean) as string[];
  const itens = c.opcionais.slice(0, 8);
  const onde = loja.cidade ? `${loja.nome}, em ${loja.cidade}` : loja.nome;
  const abertura: Record<TomTexto, string> = {
    direto: `${nome} ${ano}, ${km}.`,
    entusiasmado: `Olha que oportunidade: ${nome} ${ano} com apenas ${km}!`,
    sofisticado: `${nome} ${ano}: conforto e procedência, com ${km} rodados.`,
  };
  const fecho: Record<TomTexto, string> = {
    direto: `Veja de perto na ${onde}. Aceitamos troca e financiamos.`,
    entusiasmado: `Corre que é por pouco tempo! Venha conhecer na ${onde}. Aceitamos sua troca e fazemos o financiamento.`,
    sofisticado: `Agende sua visita na ${onde}. Avaliamos seu usado e cuidamos do financiamento.`,
  };
  if (tipo === 'post') {
    const linhas = [
      tom === 'entusiasmado' ? `🚗 Chegou: ${nome} ${ano}!` : `${nome} ${ano}`,
      `${km}${detalhes.length ? ` · ${detalhes.join(' · ')}` : ''}`,
      itens.length ? `Itens: ${itens.join(', ')}.` : null,
      preco ? `Por ${preco}.` : null,
      `Chama no direct ou no WhatsApp. ${loja.nome}`,
      `#seminovos #${c.marca.toLowerCase().replace(/\W/g, '')} #${c.modelo.toLowerCase().replace(/\W/g, '')}${loja.cidade ? ` #${loja.cidade.toLowerCase().replace(/\W/g, '')}` : ''}`,
    ];
    return linhas.filter(Boolean).join('\n');
  }
  const corpo = [
    abertura[tom],
    detalhes.length ? `${detalhes.join(', ').replace(/^./, (x) => x.toUpperCase())}.` : null,
    itens.length ? `Principais itens: ${itens.join(', ')}.` : null,
    tipo === 'anuncio' && preco ? `Preço: ${preco}.` : null,
    tipo === 'descricao_site'
      ? 'Carro revisado e com documentação em dia, pronto para a transferência.'
      : null,
    fecho[tom],
  ];
  return corpo.filter(Boolean).join(' ');
}

/** Item de mídia com IA como a API devolve ao painel. */
export interface ItemMidiaIa {
  id: string;
  tipo: TipoMidiaIa;
  status: StatusMidiaIa;
  opcoes: { fundo?: FundoFoto; enquadrar?: boolean; luz?: boolean; tom?: TomTexto };
  texto: string | null;
  motivo: string | null;
  provedor: string | null;
  custoMicros: number;
  criadoEm: string;
  concluidoEm: string | null;
  fotoId: string | null;
  antesUrl: string | null;
  depoisUrl: string | null;
  fotoResultadoId: string | null;
}

export interface UsoMidiaIa {
  limite: number;
  usados: number;
  restantes: number;
  custoMesMicros: number;
}
